import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Stack,
  Alert
} from '@mui/material';
import {
  Close,
  Download,
  Refresh,
  AttachMoney,
  TrendingDown,
  AccountBalance,
  Receipt,
  Event as EventIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import './CashTrackerPanel.css';

// For the fetch callbacks: the hook's `t` changes with the language, and as a dependency it would
// refetch the summary each time the language is switched.
const tCash = (key, options) => i18n.t(key, { ns: 'sell', ...options });

const CashTrackerPanel = ({ open, onClose, refreshTrigger }) => {
  const { t } = useTranslation('sell');
  const { getAccessToken } = useAuth();
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [exporting, setExporting] = useState(false);

  const getDateParams = useCallback(() => {
    const today = new Date();
    let startDate, endDate;

    switch (dateFilter) {
      case 'today':
        startDate = today.toISOString().split('T')[0];
        endDate = startDate;
        break;
      case 'week': {
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      }
      case 'custom':
        startDate = customStartDate || today.toISOString().split('T')[0];
        endDate = customEndDate || today.toISOString().split('T')[0];
        break;
      default:
        startDate = today.toISOString().split('T')[0];
        endDate = startDate;
    }
    return { startDate, endDate };
  }, [dateFilter, customStartDate, customEndDate]);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      const { startDate, endDate } = getDateParams();

      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/volunteer/cash-summary?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setSummary(data.data);
        } else {
          setError(translateServerMessage(data.message, tCash('cash.errors.summaryFallback')));
        }
      } else {
        setError(tCash('cash.errors.summary'));
      }
    } catch (err) {
      console.error('Error fetching cash summary:', err);
      setError(tCash('cash.errors.network'));
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, getDateParams]);

  const fetchTransactions = useCallback(async (pageNum = 0) => {
    try {
      setTransactionsLoading(true);
      const token = await getAccessToken();
      const { startDate, endDate } = getDateParams();

      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/volunteer/cash-transactions?startDate=${startDate}&endDate=${endDate}&page=${pageNum}&size=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data) {
          setTransactions(data.data.content || []);
          setTotalPages(data.data.totalPages || 0);
          setPage(pageNum);
        }
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, [getAccessToken, getDateParams]);

  useEffect(() => {
    if (open) {
      fetchSummary();
      fetchTransactions(0);
    }
  }, [open, fetchSummary, fetchTransactions]);

  useEffect(() => {
    if (open && refreshTrigger > 0) {
      fetchSummary();
      fetchTransactions(0);
    }
  }, [refreshTrigger, open, fetchSummary, fetchTransactions]);

  const handleDateFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setDateFilter(newFilter);
    }
  };

  useEffect(() => {
    if (open && dateFilter !== 'custom') {
      fetchSummary();
      fetchTransactions(0);
    }
  }, [dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      fetchSummary();
      fetchTransactions(0);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = await getAccessToken();
      const { startDate, endDate } = getDateParams();

      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/volunteer/cash-report/export?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cash-report-${startDate}-to-${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error exporting report:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleRefresh = () => {
    fetchSummary();
    fetchTransactions(page);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString(intlLocale(), {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: 'cash-tracker-drawer'
      }}
    >
      <Box className="cash-tracker-panel">
        {/* Header */}
        <Box className="cash-tracker-panel__header">
          <Typography variant="h6" className="cash-tracker-panel__title">
            <AttachMoney fontSize="small" /> {t('cash.panel.title')}
          </Typography>
          <Box>
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              <Refresh />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Date Filter */}
        <Box className="cash-tracker-panel__filters">
          <ToggleButtonGroup
            value={dateFilter}
            exclusive
            onChange={handleDateFilterChange}
            size="small"
            fullWidth
          >
            <ToggleButton value="today">{t('cash.panel.today')}</ToggleButton>
            <ToggleButton value="week">{t('cash.panel.thisWeek')}</ToggleButton>
            <ToggleButton value="custom">{t('cash.panel.custom')}</ToggleButton>
          </ToggleButtonGroup>

          {dateFilter === 'custom' && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                type="date"
                size="small"
                label={t('cash.panel.from')}
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                size="small"
                label={t('cash.panel.to')}
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Button size="small" variant="contained" onClick={handleCustomDateApply}>
                {t('cash.panel.apply')}
              </Button>
            </Stack>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {loading ? (
          <Box className="cash-tracker-panel__loading">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box className="cash-tracker-panel__summary">
              <Card className="cash-tracker-card cash-tracker-card--collected">
                <CardContent>
                  <Box className="cash-tracker-card__icon">
                    <AttachMoney />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash.panel.collected')}
                  </Typography>
                  <Typography variant="h5" className="cash-tracker-card__amount">
                    {formatCurrency(summary?.totalCashCollected)}
                  </Typography>
                </CardContent>
              </Card>

              <Card className="cash-tracker-card cash-tracker-card--refunded">
                <CardContent>
                  <Box className="cash-tracker-card__icon">
                    <TrendingDown />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash.panel.refunded')}
                  </Typography>
                  <Typography variant="h5" className="cash-tracker-card__amount">
                    {formatCurrency(summary?.totalRefunded)}
                  </Typography>
                </CardContent>
              </Card>

              <Card className="cash-tracker-card cash-tracker-card--net">
                <CardContent>
                  <Box className="cash-tracker-card__icon">
                    <AccountBalance />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash.panel.netExpected')}
                  </Typography>
                  <Typography variant="h5" className="cash-tracker-card__amount">
                    {formatCurrency(summary?.netCashExpected)}
                  </Typography>
                </CardContent>
              </Card>

              <Card className="cash-tracker-card cash-tracker-card--count">
                <CardContent>
                  <Box className="cash-tracker-card__icon">
                    <Receipt />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash.panel.transactions')}
                  </Typography>
                  <Typography variant="h5" className="cash-tracker-card__amount">
                    {summary?.totalTransactions || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('cash.panel.ticketCount', { count: summary?.totalTicketsSold || 0 })}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Event Breakdown */}
            {summary?.eventBreakdown && summary.eventBreakdown.length > 0 && (
              <Box className="cash-tracker-panel__section">
                <Typography variant="subtitle2" className="cash-tracker-panel__section-title">
                  <EventIcon fontSize="small" /> {t('cash.panel.byEvent')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('cash.panel.table.event')}</TableCell>
                        <TableCell align="right">{t('cash.panel.table.sales')}</TableCell>
                        <TableCell align="right">{t('cash.panel.table.refunds')}</TableCell>
                        <TableCell align="right">{t('cash.panel.table.net')}</TableCell>
                        <TableCell align="right">{t('cash.panel.table.tickets')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.eventBreakdown.map((event) => (
                        <TableRow key={event.eventId}>
                          <TableCell className="cash-tracker-event-name">
                            {translateServerMessage(event.eventName)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(event.amount)}</TableCell>
                          <TableCell align="right" sx={{ color: event.refundedAmount > 0 ? 'error.main' : 'text.secondary' }}>
                            {event.refundedAmount > 0 ? `-${formatCurrency(event.refundedAmount)}` : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(event.netAmount)}
                          </TableCell>
                          <TableCell align="right">{event.tickets}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Recent Transactions */}
            <Box className="cash-tracker-panel__section">
              <Typography variant="subtitle2" className="cash-tracker-panel__section-title">
                <Receipt fontSize="small" /> {t('cash.panel.recent')}
              </Typography>

              {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : transactions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  {t('cash.panel.empty')}
                </Typography>
              ) : (
                <>
                  <Box className="cash-tracker-panel__transactions">
                    {transactions.map((tx, index) => (
                      <Box key={`${tx.orderId}-${index}`} className="cash-tracker-transaction">
                        <Box className="cash-tracker-transaction__info">
                          <Typography variant="body2" fontWeight={500}>
                            {translateServerMessage(tx.eventName)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            IBO: {tx.iboNumber} &bull; {formatTime(tx.timestamp)}
                          </Typography>
                        </Box>
                        <Box className="cash-tracker-transaction__amount">
                          <Chip
                            label={tx.type === 'SALE' ? formatCurrency(tx.amount) : `-${formatCurrency(Math.abs(tx.amount))}`}
                            size="small"
                            color={tx.type === 'SALE' ? 'success' : 'error'}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {t('cash.panel.ticketCount', { count: tx.ticketCount })}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {totalPages > 1 && (
                    <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        disabled={page === 0}
                        onClick={() => fetchTransactions(page - 1)}
                      >
                        {t('cash.panel.previous')}
                      </Button>
                      <Typography variant="caption" sx={{ lineHeight: '30px' }}>
                        {page + 1} / {totalPages}
                      </Typography>
                      <Button
                        size="small"
                        disabled={page >= totalPages - 1}
                        onClick={() => fetchTransactions(page + 1)}
                      >
                        {t('cash.panel.next')}
                      </Button>
                    </Stack>
                  )}
                </>
              )}
            </Box>
          </>
        )}

        {/* Footer Actions */}
        <Box className="cash-tracker-panel__footer">
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
            disabled={exporting || loading}
            fullWidth
          >
            {exporting ? t('cash.panel.downloading') : t('cash.panel.download')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CashTrackerPanel;
